
import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import type { KeycloakConfig } from 'keycloak-js';
import type { IAuthService, IAuthUser } from '../../auth/auth.interface';
import { environment } from 'src/environments/environment';
import { StorageService } from './token-storage.service';
import { Router } from '@angular/router';
import { BaseHttpService } from './basehttp.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { LogoutServive } from 'src/app/logout/logout.service';
import { PostLoginService } from './post-login.service';

@Injectable({
  providedIn: 'root',
})
export class KeycloakAuthService implements IAuthService {

  private keycloak: Keycloak;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private storage: StorageService,
    private router: Router,
    private httpService: BaseHttpService,
    private logoutservice: LogoutServive,
    private postLoginService: PostLoginService
  ) {
    /*
     * Keycloak configuration is optional because some deployments
     * use SiteMinder or Cognito instead of Keycloak.
     *
     * Using a local optional configuration object prevents TypeScript
     * from requiring environment.keycloak in SiteMinder environments.
     *
     * This does NOT change or interact with SiteMinder configuration.
     */
    const optionalEnvironment = environment as typeof environment & {
      keycloak?: {
        url?: string;
        realm?: string;
        clientId?: string;
      };
    };

    const keycloakEnvironment = optionalEnvironment.keycloak;

    const keycloakConfig: KeycloakConfig = {
      url: keycloakEnvironment?.url ?? '',
      realm: keycloakEnvironment?.realm ?? '',
      clientId: keycloakEnvironment?.clientId ?? '',
    };

    this.keycloak = new Keycloak(keycloakConfig);

    this.keycloak.onAuthSuccess = () => {
      this.storage.set('authToken', this.keycloak.token || '');
      this.storage.set('kcToken', this.keycloak.token || '');
      this.storage.set('kcRefreshToken', this.keycloak.refreshToken || '');

      this.startTokenRefreshInterval();
    };

    this.keycloak.onAuthRefreshSuccess = () => {
      this.storage.set('authToken', this.keycloak.token || '');
      this.storage.set('kcToken', this.keycloak.token || '');
      this.storage.set('kcRefreshToken', this.keycloak.refreshToken || '');
    };

    this.keycloak.onAuthLogout = () => {
      this.storage.remove('authToken');
      this.storage.remove('kcToken');
      this.storage.remove('kcRefreshToken');
    };
  }

  async getUser(): Promise<any> {
    const {
      username,
      id,
      email,
      attributes
    } = await this.keycloak.loadUserProfile();

    return {
      id,
      username,
      email,
      attributes
    };
  }

  /**
   * Initialises the Keycloak SDK.
   * Uses login-required when Keycloak is the configured provider.
   */
  async initialize(): Promise<boolean> {
    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'login-required',
        silentCheckSsoRedirectUri:
          `${window.location.origin}/assets/silent-check-sso.html`,
        pkceMethod: 'S256',
      });

      if (authenticated) {
        /*
         * Persist tokens to session storage so existing interceptor /
         * guard code that reads sessionStorage works without changes.
         */
        this.persistTokens();
        this.registerTokenRefreshCallbacks();

        const user = await this.getUser();
        const userEmail =
          user?.email ||
          user?.username ||
          '';

        await this.postLoginService.handlePostLoginSetup(userEmail);
      }

      return authenticated;
    } catch {
      return false;
    }
  }

  /**
   * Redirects the browser to Keycloak's hosted login page.
   */
  async signIn(_user: IAuthUser): Promise<any> {
    return this.keycloak.login({
      redirectUri: window.location.origin,
      loginHint: _user.email,
    });
  }

  /**
   * Signs the user out via Keycloak and clears local session storage.
   */
  signOut(): Promise<any> {
    this.clearRefreshInterval();
    this.storage.clear();

    return this.keycloak.logout({
      redirectUri: window.location.origin
    });
  }

  /**
   * Returns true when keycloak-js reports the session as authenticated.
   */
  isAuthenticated(): Promise<boolean> {
    return Promise.resolve(
      this.keycloak.authenticated ?? false
    );
  }

  /**
   * Returns the Keycloak ID token.
   */
  getIdToken(): string | null {
    return this.keycloak.idToken ?? null;
  }

  /**
   * Returns the Keycloak access token.
   */
  getAccessToken(): string | null {
    return this.keycloak.token ?? null;
  }

  /**
   * Returns true if the Keycloak access token is still valid.
   */
  isTokenValid(): boolean {
    if (!this.keycloak.authenticated) {
      return false;
    }

    if (this.keycloak.isTokenExpired(180)) {
      this.keycloak
        .updateToken(180)
        .then(() => {
          this.persistTokens();
        })
        .catch(() => {
          this.router.navigate(['/login']);
        });
    }

    const isTokenValid =
      !this.keycloak.isTokenExpired(0);

    if (isTokenValid) {
      this.logoutservice.setUserLoginStatus(true);
    }

    return isTokenValid;
  }

  /**
   * Starts a one-minute interval that proactively refreshes
   * the Keycloak token when required.
   */
  startTokenRefreshInterval(): void {
    this.clearRefreshInterval();

    this.refreshIntervalId = setInterval(
      async () => {
        if (
          this.keycloak.authenticated &&
          this.keycloak.isTokenExpired(180)
        ) {
          try {
            const refreshed =
              await this.keycloak.updateToken(180);

            if (refreshed) {
              this.persistTokens();
            }
          } catch {
            this.router.navigate(['/login']);
          }
        }
      },
      60_000
    );
  }

  /**
   * Stores the current Keycloak tokens.
   */
  private persistTokens(): void {
    if (this.keycloak.idToken) {
      this.storage.set(
        'kcIdToken',
        this.keycloak.idToken
      );
    }

    if (this.keycloak.token) {
      this.storage.set(
        'kcAccessToken',
        this.keycloak.token
      );
    }
  }

  /**
   * Attaches Keycloak lifecycle callbacks for automatic token refresh.
   */
  private registerTokenRefreshCallbacks(): void {
    this.keycloak.onTokenExpired = () => {
      this.keycloak
        .updateToken(60)
        .then(() => {
          this.persistTokens();
        })
        .catch(() => {
          this.router.navigate(['/login']);
        });
    };
  }

  private clearRefreshInterval(): void {
    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }
}
